import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from '../../../hooks/use-toast';
import { Loader2, Search, Download, ShieldAlert, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { QueryChips, StatusCodeBadge, formatActivityDate, downloadCsv } from './activityShared';

const th = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap';
const inputCls = 'px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const REASONS = {
  no_token: { label: 'No token', cls: 'bg-gray-100 text-gray-700' },
  invalid_token: { label: 'Invalid token', cls: 'bg-red-100 text-red-800' },
  expired_token: { label: 'Expired token', cls: 'bg-yellow-100 text-yellow-800' },
  invalid_api_key: { label: 'Invalid API key', cls: 'bg-red-100 text-red-800' },
  forbidden: { label: 'Forbidden', cls: 'bg-orange-100 text-orange-800' },
  unauthorized: { label: 'Unauthorized', cls: 'bg-red-100 text-red-800' }
};

export const ReasonBadge = ({ reason }) => {
  const r = REASONS[reason] || { label: reason, cls: 'bg-gray-100 text-gray-700' };
  return <span data-testid={`rejected-reason-${reason}`} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${r.cls}`}>{r.label.toUpperCase()}</span>;
};

const Stat = ({ label, value, testId, accent }) => (
  <div data-testid={testId} className="rounded-lg border px-3 py-2" style={{ borderColor: 'var(--border-color)' }}>
    <div className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--text-muted-color)' }}>{label}</div>
    <div className={`text-sm font-semibold ${accent ? 'text-red-600' : ''}`} style={accent ? {} : { color: 'var(--text-color)' }}>{value}</div>
  </div>
);

export const RejectedRequestsTab = () => {
  const { token, backendUrl } = useAuth();
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ search: '', reason: 'all', start_date: '', end_date: '' });
  const [sort, setSort] = useState('desc');
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);
  const perPage = 50;
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const buildParams = useCallback((p, pp) => {
    const params = new URLSearchParams({ page: p, per_page: pp, sort });
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.append(k, v); });
    return params;
  }, [filters, sort]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const t = setTimeout(() => {
      Promise.all([
        axios.get(`${backendUrl}/api/audit/rejected-requests?${buildParams(page, perPage)}`, H),
        axios.get(`${backendUrl}/api/audit/rejected-requests/summary`, H)
      ])
        .then(([r, s]) => {
          setItems(r.data.items || []); setTotal(r.data.total || 0); setReasons(r.data.reasons || []); setSummary(s.data);
          // Rows are now displayed → reset this admin's "new rejected requests" counter
          axios.post(`${backendUrl}/api/audit/rejected-requests/seen`, {}, H)
            .then(() => window.dispatchEvent(new CustomEvent('rejected-requests-seen')))
            .catch(() => {});
        })
        .catch(() => { setItems([]); setTotal(0); })
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, backendUrl, page, buildParams, refreshTick]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const topReason = summary && Object.entries(summary.by_reason || {}).sort((a, b) => b[1] - a[1])[0];
  const topIp = summary?.top_ips?.[0];

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${backendUrl}/api/audit/rejected-requests?${buildParams(1, 1000)}`, H);
      downloadCsv(`rejected_requests_${new Date().toISOString().slice(0, 10)}.csv`,
        ['Timestamp', 'IP', 'Reason', 'Status', 'Method', 'Path', 'Query', 'User', 'API key', 'User Agent'],
        (r.data.items || []).map((i) => [formatActivityDate(i.timestamp), i.ip_address, i.reason, i.status_code, i.method, i.path, Object.entries(i.query || {}).map(([k, v]) => `${k}=${v}`).join(' '), i.email || i.user_id || '', i.api_key_hint || '', i.user_agent || '']));
    } catch (e) {
      toast({ title: 'Export failed', description: 'Could not export rejected requests', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rejected-requests-tab">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="New since last view" value={(summary.unseen ?? 0).toLocaleString()} testId="rejected-stat-unseen" accent={summary.unseen > 0} />
          <Stat label="Rejected (24h)" value={summary.last_24h.toLocaleString()} testId="rejected-stat-24h" />
          <Stat label="Top reason (24h)" value={topReason ? `${REASONS[topReason[0]]?.label || topReason[0]} · ${topReason[1]}` : '—'} testId="rejected-stat-reason" />
          <Stat label="Top IP (24h)" value={topIp ? `${topIp.ip} · ${topIp.count}` : '—'} testId="rejected-stat-ip" />
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input data-testid="rejected-search" value={filters.search} onChange={(e) => setFilter('search', e.target.value)}
                placeholder="Search by IP, path, user, API key or user agent…" className={`${inputCls} w-full pl-10`} />
            </div>
            <select data-testid="rejected-reason-filter" value={filters.reason} onChange={(e) => setFilter('reason', e.target.value)} className={inputCls}>
              <option value="all">All reasons</option>
              {reasons.map((r) => <option key={r} value={r}>{REASONS[r]?.label || r}</option>)}
            </select>
            <input data-testid="rejected-start-date" type="date" value={filters.start_date} onChange={(e) => setFilter('start_date', e.target.value)} className={inputCls} />
            <input data-testid="rejected-end-date" type="date" value={filters.end_date} onChange={(e) => setFilter('end_date', e.target.value)} className={inputCls} />
            <Button data-testid="rejected-refresh" onClick={() => setRefreshTick((t) => t + 1)} disabled={loading} variant="outline" className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button data-testid="rejected-export" onClick={exportCsv} disabled={exporting || loading || items.length === 0} variant="outline" className="flex items-center gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2" style={{ color: 'var(--text-color)' }}><ShieldAlert className="h-5 w-5 text-red-500" /> Rejected Requests</span>
            <span data-testid="rejected-total" className="text-sm font-normal" style={{ color: 'var(--text-muted-color)' }}>{total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--button-color)' }} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12" data-testid="rejected-empty">
              <ShieldAlert className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted-color)' }} />
              <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>No rejected requests</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--text-muted-color)' }}>API calls refused with 401/403 (missing, invalid or expired tokens, bad API keys, forbidden access) will appear here.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" data-testid="rejected-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={th} style={{ color: 'var(--filter-label-color)' }}>
                        <button data-testid="rejected-sort-date" onClick={() => { setSort((s) => (s === 'desc' ? 'asc' : 'desc')); setPage(1); }} className="inline-flex items-center gap-1 uppercase hover:underline">
                          Timestamp {sort === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                        </button>
                      </th>
                      {['IP Address', 'Reason', 'Result', 'Request', 'Query', 'User / Key', 'User Agent'].map((h) => (
                        <th key={h} className={th} style={{ color: 'var(--filter-label-color)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((it) => (
                      <tr key={it.id} className="hover:bg-gray-50" data-testid="rejected-row">
                        <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>{formatActivityDate(it.timestamp)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono" style={{ color: 'var(--text-color)' }}>{it.ip_address}</td>
                        <td className="px-4 py-3 whitespace-nowrap"><ReasonBadge reason={it.reason} /></td>
                        <td className="px-4 py-3 whitespace-nowrap"><StatusCodeBadge code={it.status_code} /></td>
                        <td className="px-4 py-3 text-sm font-mono text-xs" style={{ color: 'var(--text-color)' }}><span className="font-semibold">{it.method}</span> {it.path}</td>
                        <td className="px-4 py-3 max-w-xs"><QueryChips query={it.query} /></td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>{it.email || it.user_id || it.api_key_hint || <span style={{ color: 'var(--text-muted-color)' }}>anonymous</span>}</td>
                        <td className="px-4 py-3 text-xs max-w-xs truncate" title={it.user_agent} style={{ color: 'var(--text-muted-color)' }}>{it.user_agent || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="text-sm" style={{ color: 'var(--text-muted-color)' }}>Page {page} of {totalPages}</div>
                  <div className="flex gap-2">
                    <button data-testid="rejected-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 border rounded-lg disabled:opacity-50" style={{ borderColor: 'var(--border-color)', color: 'var(--button-color)' }}>Previous</button>
                    <button data-testid="rejected-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 border rounded-lg disabled:opacity-50" style={{ borderColor: 'var(--border-color)', color: 'var(--button-color)' }}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RejectedRequestsTab;
